import type { Options } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { Graph } from '@/components/Graph';

// Sanitization schema for AI/backend-generated markdown (#4828). Content
// reaching this component embeds scraped third-party text, so raw HTML must
// be sanitized after rehypeRaw parses it — otherwise <img onerror>/<iframe>
// injected via a scraped site executes in the user's session (stored XSS).
// The schema extends the default allowlist with the markup our report
// generators legitimately emit (trend spans, scorecard blocks).
export const markdownSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ['className', 'trend-improving', 'trend-steady', 'trend-declining'],
      'style',
    ],
    div: [...(defaultSchema.attributes?.div ?? []), 'dataScorecard', 'style'],
  },
};

// `style` is allowed on span/div so the server-generated scorecard block
// (purple hero box, CSS grid) renders — including historical reports already
// persisted with inline styles. But arbitrary CSS is itself an XSS/UI-redress
// vector: an attacker steering the LLM to emit `position:fixed;inset:0` could
// overlay the page (clickjacking/phishing), and legacy engines execute
// `expression()`/`-moz-binding`/`url(javascript:)`. So every surviving `style`
// is filtered to a safe property allowlist below — layout/typography only,
// no positioning, no `url()`/`expression()`. This runs AFTER rehypeSanitize
// (which preserves the `style` string verbatim) on the HAST tree.
const SAFE_STYLE_PROPERTIES = new Set<string>([
  'color',
  'background',
  'background-color',
  'opacity',
  'font-size',
  'font-weight',
  'font-style',
  'font-family',
  'text-align',
  'text-transform',
  'text-decoration',
  'line-height',
  'letter-spacing',
  'white-space',
  'vertical-align',
  'margin',
  'margin-top',
  'margin-bottom',
  'margin-left',
  'margin-right',
  'padding',
  'padding-top',
  'padding-bottom',
  'padding-left',
  'padding-right',
  'border',
  'border-top',
  'border-bottom',
  'border-left',
  'border-right',
  'border-color',
  'border-width',
  'border-style',
  'border-radius',
  'width',
  'min-width',
  'max-width',
  'height',
  'min-height',
  'max-height',
  'overflow',
  'overflow-x',
  'overflow-y',
  'display',
  'grid-template-columns',
  'grid-template-rows',
  'grid-column',
  'grid-row',
  'gap',
  'row-gap',
  'column-gap',
  'align-items',
  'justify-content',
  'justify-items',
  'flex',
  'flex-direction',
  'flex-wrap',
]);

// Reject values that can smuggle script/network/escape regardless of property.
const UNSAFE_STYLE_VALUE = /(url\(|expression\(|javascript:|@import|behaviou?r:|-moz-binding|<|\\)/i;

function sanitizeStyleString(style: string): string {
  return style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .filter((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator === -1) return false;
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      return SAFE_STYLE_PROPERTIES.has(property) && !UNSAFE_STYLE_VALUE.test(value);
    })
    .join('; ');
}

interface HastNode {
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

function filterStyleAttributes(node: HastNode): void {
  if (node.properties && typeof node.properties.style === 'string') {
    const cleaned = sanitizeStyleString(node.properties.style);
    if (cleaned) {
      node.properties.style = cleaned;
    } else {
      delete node.properties.style;
    }
  }
  node.children?.forEach(filterStyleAttributes);
}

// rehype plugin: prune unsafe CSS from every `style` left by rehypeSanitize.
function rehypeFilterStyles() {
  return (tree: HastNode): void => filterStyleAttributes(tree);
}

// Shared pipeline for rendering AI/server-generated markdown safely. Order
// matters: parse raw HTML, sanitize the tree, then prune unsafe inline CSS.
export const markdownRehypePlugins: Options['rehypePlugins'] = [
  rehypeRaw,
  [rehypeSanitize, markdownSanitizeSchema],
  rehypeFilterStyles,
];

// Custom code block renderer to handle graph blocks
interface PreProps {
  children?: React.ReactNode;
  [key: string]: unknown;
}

// For code blocks (```), ReactMarkdown wraps them in <pre><code>...</code></pre>
// We intercept at the pre level to handle graph blocks
const CustomPre = ({ children, ...props }: PreProps) => {
  // Check if this is a code block with language
  if (children && typeof children === 'object' && 'props' in children) {
    const childProps = children.props;
    const className = childProps?.className || '';
    const match = /language-(\w+)/.exec(className);
    const language = match ? match[1] : '';

    if (language === 'graph') {
      try {
        const content = childProps?.children;
        const configString =
          typeof content === 'string' ? content.replace(/\n$/, '') : String(content).replace(/\n$/, '');
        const graphConfig = JSON.parse(configString);
        return <Graph config={graphConfig} className='my-6' />;
      } catch (error) {
        console.error('Failed to parse graph configuration:', error);
        return (
          <div className='bg-red-50 border border-red-200 rounded p-4 my-4'>
            <p className='text-red-600 text-sm'>Error rendering chart: Invalid JSON configuration</p>
            <details className='mt-2'>
              <summary className='cursor-pointer text-xs text-red-500'>Show raw content</summary>
              <pre className='mt-1 text-xs bg-red-100 p-2 rounded overflow-auto'>{String(childProps?.children)}</pre>
            </details>
          </div>
        );
      }
    }
  }

  // Default pre rendering
  return <pre {...props}>{children}</pre>;
};

interface MarkdownContentProps {
  content: string;
  className?: string;
}

export function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    <div className={`text-md leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-900 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-6 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-8 [&_h3]:text-base [&_h3]:font-medium [&_h3]:mb-2 [&_h3]:mt-4 [&_li]:mb-1 [&_ul]:mb-4 [&_ul:last-child]:mb-0 [&_ul]:ml-6 [&_ul]:list-disc [&_ol]:mb-4 [&_ol:last-child]:mb-0 [&_ol]:ml-6 [&_ol]:list-decimal [&_a]:text-[#290BD9] [&_a]:underline [&_a:hover]:text-[#2309C5] [&_hr]:hidden [&_table]:w-full [&_table]:border-collapse [&_table]:mb-4 [&_th]:border [&_th]:border-gray-300 [&_th]:px-4 [&_th]:py-2 [&_th]:bg-gray-50 [&_th]:font-semibold [&_th]:text-center [&_td]:border [&_td]:border-gray-300 [&_td]:px-4 [&_td]:py-2 [&_td]:text-center [&_td:first-child]:!text-left [&>*:first-child]:mt-0 [&_.trend-improving]:text-[#059669] [&_.trend-steady]:text-[#D97706] [&_.trend-declining]:text-[#DC2626] [&_[data-scorecard]]:text-white [&_[data-scorecard]_*]:text-white ${className || ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={markdownRehypePlugins}
        components={{
          pre: CustomPre as never,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
